import { create } from "zustand";
import {
  ChatMessage,
  ChatSession,
  ChatSessionType,
  Decor8PrimeWallsRequest,
  GenerateImageDecor8Request,
  GenerateTextToSpeechRequest,
  IRequestImage,
  PresentationRequest,
  AgentMessageRequest,
} from "../types";
import { chatAPI, aiAPI, userAPI } from "../services";
import i18n from "../i18n/config";
import { ChatState } from "../types";
import fs from "fs";
export const useChatStore = create<ChatState>((set, get) => ({
  sessions: [],
  currentSession: null,
  models: [],
  selectedModel: "llama-3.3-70b-versatile",
  isLoading: false,
  error: null,
  input: "",
  promptImage: "",
  lastPromptImage: "",
  promptImageFile: null,
  currentPage: 1,
  hasMoreMessages: false,
  isLoadingMore: false,
  folders: [],

  loadSessions: async () => {
    set({ isLoading: true, error: null });
    try {
      const sessionsData = await chatAPI.getSessions();
      const sessions = sessionsData.map(
        ({
          id,
          sessionName,
          firstMessage,
          lastMessageAt,
          sessionType,
          indexes,
        }: {
          id: string;
          sessionName: string | null;
          firstMessage: string;
          lastMessageAt: string;
          sessionType: ChatSessionType;
          indexes: number[];
        }) => ({
          id,
          sessionName,
          sessionType,
          messages: [],
          createdAt: lastMessageAt || new Date().toISOString(),
          indexes,
        })
      );
      set({ sessions, isLoading: false });
    } catch (error) {
      console.error("Error fetching sessions:", error);
      set({ error: "Failed to load chat sessions", isLoading: false });
    }
  },

  updateSessionIndexes: async (id: string, indexes: number) => {
    set({ isLoading: true, error: null });
    await chatAPI.updateSessionIndexes(id, indexes);
    set({ isLoading: false });
    await get().loadSessionMessages(id);
  },
  sendDecor8Message: async (request: GenerateImageDecor8Request) => {
    const { currentSession } = get();
    if (!currentSession) {
      await get().createSession(ChatSessionType.IMAGE_CHAT);
      return;
    }
    try {
      set({ isLoading: true, error: null });
      const response = await aiAPI.generateImageDecor8(request);
      set({ isLoading: false });
    } catch (error) {
      set({ error: "Failed to generate image", isLoading: false });
    }
  },
  sendDecor8PrimeWallsMessage: async (request: Decor8PrimeWallsRequest) => {
    const { currentSession } = get();
    if (!currentSession) {
      await get().createSession(ChatSessionType.IMAGE_CHAT);
      return;
    }
    try {
      set({ isLoading: true, error: null });
      const response = await aiAPI.generateImageDecor8PrimeWalls(request);
      set({ isLoading: false });
    } catch (error) {
      set({ error: "Failed to generate image", isLoading: false });
    }
  },
  sendImageMessage: async (request: IRequestImage) => {
    const { currentSession } = get();
    if (!currentSession) {
      await get().createSession(ChatSessionType.IMAGE_CHAT);
      return;
    }
    try {
      set({ isLoading: true, error: null });
      const response = await aiAPI.generateImageRunware(
        request,
        currentSession.id
      );
      set({ isLoading: false });
    } catch (error) {
      set({ error: "Failed to generate image", isLoading: false });
    }
  },

  sendAudioMessage: async (request: GenerateTextToSpeechRequest) => {
    const { currentSession } = get();
    if (!currentSession) {
      await get().createSession(ChatSessionType.AUDIO_CHAT);
      return;
    }
    try {
      set({ isLoading: true, error: null });
      await aiAPI.generateAudioTTS(request, currentSession.id);
      set({ isLoading: false });
    } catch (error) {
      set({ error: "Failed to generate audio", isLoading: false });
    }
  },

  sendAgentMessage: async (request: AgentMessageRequest) => {
    const { currentSession } = get();
    if (!currentSession) {
      await get().createSession(ChatSessionType.AGENT_CHAT);
      return;
    }
    try {
      set({ isLoading: true, error: null });
      const response = await aiAPI.generateAgentMessage(request);
      set({ isLoading: false });
    } catch (error) {
      set({ error: "Failed to generate agent message", isLoading: false });
    }
  },

  sendPresentationMessage: async (request: PresentationRequest) => {
    const { currentSession } = get();

    if (!currentSession) {
      await get().createSession(ChatSessionType.STANDARD);
      return;
    }

    try {
      set({ isLoading: true, error: null });
      const response = await aiAPI.generatePresentation(request);

      // Get the response as an array buffer instead of text
      const arrayBuffer = await response;
      const blob = new Blob([arrayBuffer], {
        type: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      });

      // Download file
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `presentation-${Date.now()}.pptx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      // Store in chat
      const presentationMessage: ChatMessage = {
        id: crypto.randomUUID(),
        sessionId: get().currentSession!.id,
        role: "assistant",
        content: "Here's your generated presentation:",
        presentationData: arrayBuffer,
        timestamp: new Date().toISOString(),
        createdAt: new Date().toISOString(),
      };

      set((state) => ({
        currentSession: {
          ...state.currentSession!,
          messages: [
            ...(state.currentSession?.messages || []),
            presentationMessage,
          ],
        },
        input: "",
        isLoading: false,
      }));

      return blob;
    } catch (error) {
      console.error("Error generating presentation:", error);
      set({ error: "Failed to generate presentation", isLoading: false });
      throw error;
    }
  },

  createSession: async (
    sessionType: ChatSessionType = ChatSessionType.STANDARD
  ) => {
    set({ isLoading: true, error: null });
    try {
      const { id } = await chatAPI.createSession(sessionType);
      const newSession: ChatSession = {
        id,
        messages: [],
        createdAt: new Date().toISOString(),
        sessionType,
        indexes: ChatSessionType.COMPARATIVE_CHAT === sessionType ? 2 : 1,
      };
      set((state) => ({
        sessions: [newSession, ...state.sessions],
        currentSession: newSession,
        isLoading: false,
      }));
      return newSession;
    } catch (error) {
      set({ error: "Failed to create new chat session", isLoading: false });
      throw error;
    }
  },

  deleteSession: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      await chatAPI.deleteSession({ id });
      set((state) => ({
        sessions: state.sessions.filter((s) => s.id !== id),
        currentSession:
          state.currentSession?.id === id ? null : state.currentSession,
        isLoading: false,
      }));
    } catch (error) {
      set({ error: "Failed to delete chat session", isLoading: false });
    }
  },

  loadSessionMessages: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      const { messages, hasMore, indexes, sessionType } =
        await chatAPI.getSessionMessages({
          id,
        });
      set((state) => ({
        currentSession: {
          id,
          messages,
          sessionType,
          indexes,
          createdAt: new Date().toISOString(),
        },
        hasMoreMessages: hasMore,
        currentPage: 1,
        isLoading: false,
      }));
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
    }
  },

  loadOlderMessages: async () => {
    const state = get();
    if (
      !state.currentSession?.id ||
      state.isLoadingMore ||
      !state.hasMoreMessages
    )
      return;

    set({ isLoadingMore: true });
    try {
      const nextPage = state.currentPage + 1;
      const { messages, hasMore } = await chatAPI.getSessionMessages(
        { id: state.currentSession.id },
        nextPage
      );

      set((state) => ({
        currentSession: {
          id: state.currentSession!.id,
          messages: [...messages, ...state.currentSession!.messages],
          createdAt: state.currentSession!.createdAt,
          sessionType: state.currentSession!.sessionType,
          indexes: state.currentSession!.indexes,
        },
        hasMoreMessages: hasMore,
        currentPage: nextPage,
        isLoadingMore: false,
      }));
    } catch (error: any) {
      set({ error: error.message, isLoadingMore: false });
    }
  },

  sendMessage: async (content: string, temperature: number, memory: number) => {
    const { currentSession, selectedModel } = get();

    if (!currentSession) {
      await get().createSession(ChatSessionType.STANDARD);
      return;
    }

    try {
      // Send message and get initial messages
      const { userMessages, aiMessages } = await chatAPI.sendMessage(
        { id: currentSession.id },
        content,
        [selectedModel]
      );

      // Update UI with both messages
      set((state) => ({
        currentSession: {
          ...state.currentSession!,
          messages: [
            ...(state.currentSession?.messages || []),
            userMessages[0],
            aiMessages[0],
          ],
        },
        input: "", // Clear input after sending
      }));

      // Start streaming AI response
      let aiContent = "";
      try {
        await aiAPI.generateResponse(
          content,
          selectedModel,
          temperature,
          memory,
          currentSession.id,
          async (chunk: any) => {
            if (chunk && chunk && chunk.kwargs?.content) {
              aiContent += chunk.kwargs.content;
              // Update UI only during streaming
              set((state) => ({
                currentSession: {
                  ...state.currentSession!,
                  messages: state.currentSession!.messages.map((msg) =>
                    msg.id === aiMessages[0].id
                      ? { ...msg, content: aiContent }
                      : msg
                  ),
                },
              }));
            }
          }
        );
      } catch (error: any) {
        const errorMessage =
          error.message === "Credits limit exceeded"
            ? i18n.t("chat.errors.creditsExceeded")
            : i18n.t("chat.errors.generic");

        // Update the AI message with the error
        await chatAPI.updateAIMessage(
          { id: currentSession.id },
          aiMessages[0].id,
          errorMessage
        );

        set((state) => ({
          currentSession: {
            ...state.currentSession!,
            messages: state.currentSession!.messages.map((msg) =>
              msg.id === aiMessages[0].id
                ? { ...msg, content: errorMessage }
                : msg
            ),
          },
        }));
        return;
      }

      // Update API once at the end with final content
      await chatAPI.updateAIMessage(
        { id: currentSession.id },
        aiMessages[0].id,
        aiContent
      );
      await get().loadSessionMessages(currentSession.id);
    } catch (error) {
      console.error("Error in sendMessage:", error);
      set({ error: i18n.t("chat.errors.failed") });
    }
  },

  sendMultiMessage: async (
    content: string,
    models: string[],
    temperatures: number[],
    memories: number[]
  ) => {
    const { currentSession } = get();
    if (!currentSession) {
      await get().createSession(ChatSessionType.COMPARATIVE_CHAT);
      return;
    }

    try {
      // Send initial messages
      const { userMessages, aiMessages } = await chatAPI.sendMessage(
        { id: currentSession.id },
        content,
        models
      );

      // Initialize array to store content for each model
      const aiContents = new Array(models.length).fill("");

      // Update UI with initial messages
      set((state) => ({
        currentSession: {
          ...state.currentSession!,
          messages: [
            ...(state.currentSession?.messages || []),
            ...userMessages,
            ...aiMessages,
          ],
        },
        input: "", // Clear input after sending
      }));

      // Start streaming AI responses
      try {
        await aiAPI.generateMultiResponse(
          content,
          models,
          temperatures,
          memories,
          currentSession.id,
          async (chunk: any) => {
            if (chunk && chunk.content && typeof chunk.index === "number") {
              aiContents[chunk.index] += chunk.content;

              // Update UI with content for this specific index
              set((state) => ({
                currentSession: {
                  ...state.currentSession!,
                  messages: state.currentSession!.messages.map((msg) =>
                    msg.id === aiMessages[chunk.index].id
                      ? { ...msg, content: aiContents[chunk.index] }
                      : msg
                  ),
                },
              }));
            }
          }
        );

        // Force scroll after streaming finishes
        set((state) => ({
          currentSession: {
            ...state.currentSession!,
            messages: [...state.currentSession!.messages],
          },
        }));
      } catch (error: any) {
        const errorMessage =
          error.message === "Credits limit exceeded"
            ? i18n.t("chat.errors.creditsExceeded")
            : i18n.t("chat.errors.generic");

        // Update all AI messages with the error
        await Promise.all(
          aiMessages.map((msg: { id: string }) =>
            chatAPI.updateAIMessage(
              { id: currentSession.id },
              msg.id,
              errorMessage
            )
          )
        );

        set((state) => ({
          currentSession: {
            ...state.currentSession!,
            messages: state.currentSession!.messages.map((msg) =>
              aiMessages.some((aiMsg: { id: string }) => aiMsg.id === msg.id)
                ? { ...msg, content: errorMessage }
                : msg
            ),
          },
        }));
        return;
      }

      // Update API with final content for each model
      await Promise.all(
        aiMessages.map((msg: { id: string }, i: number) =>
          chatAPI.updateAIMessage(
            { id: currentSession.id },
            msg.id,
            aiContents[i]
          )
        )
      );

      // Force one final scroll after everything is complete
      set((state) => ({
        currentSession: {
          ...state.currentSession!,
          messages: [...state.currentSession!.messages],
        },
      }));
    } catch (error) {
      console.error("Error in sendMultiMessage:", error);
      set({ error: i18n.t("chat.errors.failed") });
    }
  },

  sendVideoMessage: async (
    promptText: string,
    promptImage: string,
    promptLastImage: string,
    duration: 5 | 10
  ) => {
    const { currentSession, selectedModel } = get();
    if (!currentSession) {
      await get().createSession(ChatSessionType.VIDEO_CHAT);
      return;
    }
    try {
      const { userMessages, aiMessages } = await chatAPI.sendMessage(
        { id: currentSession.id },
        promptText,
        [selectedModel]
      );

      // Update UI with both messages
      set((state) => ({
        currentSession: {
          ...state.currentSession!,
          messages: [
            ...(state.currentSession?.messages || []),
            userMessages[0],
            aiMessages[0],
          ],
        },
        input: "", // Clear input after sending
      }));

      const videoTask = await aiAPI.generateVideo(
        [
          { uri: promptImage, position: "first" },
          { uri: promptLastImage, position: "last" },
        ],
        promptText,
        duration,
        aiMessages[0].id
      );
      console.log("Video task:", videoTask);
      // TODO: poll for video status
    } catch (error) {
      console.error("Error in sendVideoMessage:", error);
      set({ error: i18n.t("chat.errors.failed") });
    }
  },

  loadModels: async () => {
    set({ isLoading: true, error: null });
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        set({ error: "Authentication required", isLoading: false });
        return;
      }
      const models = await aiAPI.getModels();
      // filter out disabled models
      const filteredModels = models.filter((model) => model.enabled);
      set({ models: filteredModels, isLoading: false });
    } catch (error) {
      set({ error: "Failed to load AI models", isLoading: false });
    }
  },

  setPromptImage: (promptImage: string) => {
    set({ promptImage });
  },

  setLastPromptImage: (lastPromptImage: string) => {
    set({ lastPromptImage });
  },

  setPromptImageFile: (promptImageFile: string | null) => {
    set({ promptImageFile });
  },

  setSelectedModel: (modelId: string) => {
    set({ selectedModel: modelId });
    // if image model, update lastImageModel
    const model = get().models.find((m) => m.id === modelId);
    if (model?.capabilities?.includes("image")) {
      userAPI.updatePreferences({ lastImageModel: modelId });
    } else {
      userAPI.updatePreferences({ lastModel: modelId });
    }
  },

  updateSessionName: async (id: string, name: string) => {
    set({ isLoading: true, error: null });
    try {
      await chatAPI.updateSessionName(id, name);
      set((state) => ({
        sessions: state.sessions.map((s) =>
          s.id === id ? { ...s, sessionName: name } : s
        ),
        currentSession:
          state.currentSession?.id === id
            ? { ...state.currentSession, sessionName: name }
            : state.currentSession,
        isLoading: false,
      }));
      await get().loadFolders();
    } catch (error) {
      console.error("Error updating session name:", error);
      set({ error: "Failed to update session name", isLoading: false });
    }
  },

  setInput: (content: string) => {
    set({ input: content });
  },

  loadFolders: async () => {
    try {
      const folders = await chatAPI.getFolders();
      set({ folders });
    } catch (error) {
      console.error("Error loading folders:", error);
    }
  },

  createFolder: async (name: string) => {
    try {
      await chatAPI.createFolder(name);
      await get().loadFolders();
    } catch (error) {
      console.error("Error creating folder:", error);
    }
  },

  updateFolder: async (id: string, name: string) => {
    try {
      await chatAPI.updateFolder(id, name);
      await get().loadFolders();
    } catch (error) {
      console.error("Error updating folder:", error);
    }
  },

  deleteFolder: async (id: string) => {
    try {
      await chatAPI.deleteFolder(id);
      await get().loadFolders();
    } catch (error) {
      console.error("Error deleting folder:", error);
    }
  },

  addChatToFolder: async (folderId: string, id: string) => {
    try {
      await chatAPI.addChatToFolder(folderId, id);
      await get().loadFolders();
    } catch (error) {
      console.error("Error adding chat to folder:", error);
    }
  },

  removeChatFromFolder: async (id: string) => {
    try {
      await chatAPI.removeChatFromFolder(id);
      await get().loadFolders();
    } catch (error) {
      console.error("Error removing chat from folder:", error);
    }
  },
}));
